import React, { useState, useEffect, useRef } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faCheckCircle, 
  faIdCard, 
  faChild, 
  faUserShield, 
  faCreditCard,
  faCalendarAlt,
  faStethoscope,
  faShieldAlt,
  faPhone
} from '@fortawesome/free-solid-svg-icons';
import './BulkBilling.css';

const BulkBilling = () => {
  const [animatedElements, setAnimatedElements] = useState(new Set());
  const whatIsRef = useRef(null);
  const criteriaRef = useRef(null);
  const servicesRef = useRef(null);
  const procedureFeesRef = useRef(null);
  const requirementsRef = useRef(null);
  const infoRef = useRef(null);

  useEffect(() => {
    // Intersection Observer for scroll animations
    const observerOptions = {
      threshold: 0.1,
      rootMargin: '0px 0px -50px 0px'
    };

    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          const elementId = entry.target.id;
          setAnimatedElements(prev => new Set([...prev, elementId]));
          
          if (elementId === 'what-is') {
            // Handle what-is section - fade animation
            const contentText = entry.target.querySelector('.content-text');
            const contentImage = entry.target.querySelector('.content-image');
            
            if (contentText) {
              setTimeout(() => {
                contentText.classList.add('animate-fade-left');
              }, 100);
            }
            
            if (contentImage) {
              setTimeout(() => {
                contentImage.classList.add('animate-fade-right');
              }, 300);
            }
          } else if (elementId === 'criteria') {
            // Handle criteria cards - flip animation
            const criteriaCards = entry.target.querySelectorAll('.criteria-card');
            criteriaCards.forEach((card, index) => {
              setTimeout(() => {
                card.classList.add('animate-flip');
              }, index * 200); // Staggered flip animation
            });
          } else if (elementId === 'services') {
            // Handle service table rows - slide up animation
            const tableRows = entry.target.querySelectorAll('.table-row');
            tableRows.forEach((row, index) => {
              setTimeout(() => {
                row.classList.add('animate-slide-up');
              }, index * 100); // Staggered slide up animation
            });
          } else if (elementId === 'procedures') {
            // Handle procedure fees table rows - slide up animation
            const feesTableRows = entry.target.querySelectorAll('.fees-table tbody tr');
            feesTableRows.forEach((row, index) => {
              setTimeout(() => {
                row.classList.add('animate-slide-up');
              }, index * 50); // Faster stagger for more rows
            });
            
            // Animate additional sections
            const additionalCards = entry.target.querySelectorAll('.fee-card');
            additionalCards.forEach((card, index) => {
              setTimeout(() => {
                card.classList.add('animate-slide-up');
              }, (feesTableRows.length * 50) + (index * 100));
            });
            
            const workcoverCards = entry.target.querySelectorAll('.workcover-card');
            workcoverCards.forEach((card, index) => {
              setTimeout(() => {
                card.classList.add('animate-slide-up');
              }, (feesTableRows.length * 50) + (additionalCards.length * 100) + (index * 150));
            });
            
            const noticeCard = entry.target.querySelector('.notice-card');
            if (noticeCard) {
              setTimeout(() => {
                noticeCard.classList.add('animate-slide-up');
              }, (feesTableRows.length * 50) + (additionalCards.length * 100) + (workcoverCards.length * 150) + 200);
            }
          } else if (elementId === 'requirements') {
            // Handle requirements - bounce animation
            const requirementItems = entry.target.querySelectorAll('.requirement-item');
            requirementItems.forEach((item, index) => {
              setTimeout(() => {
                item.classList.add('animate-bounce');
              }, index * 200); // Staggered bounce animation
            });
          } else if (elementId === 'info') {
            // Handle info cards - rotate animation
            const infoCards = entry.target.querySelectorAll('.info-card');
            infoCards.forEach((card, index) => {
              setTimeout(() => {
                card.classList.add('animate-rotate');
              }, index * 250); // Staggered rotate animation
            });
          }
        }
      });
    }, observerOptions);

    // Observe sections
    const sections = [whatIsRef.current, criteriaRef.current, servicesRef.current, procedureFeesRef.current, requirementsRef.current, infoRef.current];
    sections.forEach(section => {
      if (section) observer.observe(section);
    });
    
    return () => {
      observer.disconnect();
    };
  }, []);

  const bulkBillingCriteria = [
    {
      icon: faUserShield,
      title: "Pensioners & Seniors",
      description: "65 years or older AND hold a valid Pension Card or Health Care Card",
      color: "#5588BB"
    },
    {
      icon: faChild,
      title: "Children",
      description: "Children 16 years and under for routine consultations",
      color: "#2196F3"
    },
    {
      icon: faIdCard,
      title: "Concession Card Holders",
      description: "Valid Health Care Card or Pensioner Concession Card holders",
      color: "#FF9800"
    },
    {
      icon: faShieldAlt,
      title: "Medicare Card",
      description: "Not only this but everyone else with a valid Medicare card.",
      color: "#9C27B0"
    }
  ];

  const services = [
    {
      service: "Standard Consultation (Level B)",
      bulkBilled: "All Patients",
      privateRate: "N/A - Bulk Billed Only"
    },
    {
      service: "Extended Consultation (Level C)",
      bulkBilled: "All Patients",
      privateRate: "N/A - Bulk Billed Only"
    },
    {
      service: "Long Consultation (Level D)",
      bulkBilled: "All Patients",
      privateRate: "N/A - Bulk Billed Only"
    },
    {
      service: "Chronic Disease Management",
      bulkBilled: "All Patients",
      privateRate: "N/A - Bulk Billed Only"
    },
    {
      service: "Health Assessments",
      bulkBilled: "All Patients",
      privateRate: "N/A - Bulk Billed Only"
    },
    {
      service: "Immunizations & Vaccinations",
      bulkBilled: "All Patients",
      privateRate: "N/A - Bulk Billed Only"
    },
    
    {
      service: "Mental Health Care Plans",
      bulkBilled: "All Patients",
      privateRate: "N/A - Bulk Billed Only"
    }
  ];

  const requirements = [
    "Valid Medicare card must be presented at each visit.",
    "Some procedures may require additional fees."
  ];

  const procedureFees = [
    { procedure: "Iron infusions", fee: "$100" },
    { procedure: "Venesections", fee: "$110" },
    { procedure: "Ear syringing", fee: "$30" },
    { procedure: "Implanon insertion", fee: "$70" },
    { procedure: "Implanon removal", fee: "$70" },
    { procedure: "IUD insertion", fee: "$70" },
    { procedure: "IUD removal", fee: "$70" },
    { procedure: "Removal and insertion of implanon", fee: "$110" },
    { procedure: "Employment medical", fee: "$120" },
    { procedure: "Commercial license", fee: "$120" },
    { procedure: "Sutures/wound repair less than 7 cm", fee: "$50-100" },
    { procedure: "Sutures/wound repair > 7cm", fee: "$100-120" },
    { procedure: "Punch/Shave biopsy (One biopsy)", fee: "$50" },
    { procedure: "Punch/Shave biopsy (More than one)", fee: "$70" },
    { procedure: "Melanoma skin cancer excisions + after care", fee: "$120" },
    { procedure: "Non Melanoma skin cancer excisions + after care", fee: "$99" },
    { procedure: "Wound dressing", fee: "$30" },
    { procedure: "Wedge resection", fee: "$120" },
    { procedure: "Skin Flap/Graft", fee: "$200" },
    { procedure: "Medicinal cannabis initial", fee: "$200" },
    { procedure: "Medicinal cannabis repeat script", fee: "$100" }
  ];

  const additionalFees = [
    { service: "Cancellation fee", fee: "$50" },
    { service: "Rescheduling fee", fee: "$30" }
  ];

  return (
    <div className="bulk-billing">
      {/* Hero Section */}
      <section className="bulk-billing-hero">
        <div className="container">
          <div className="hero-content">
            <h1>Bulk Billing Services</h1>
            <p>Quality General Practitioner accessible to all - No out-of-pocket expenses for all patients with valid medical card</p>
            <div className="hero-features">
              <div className="feature">
                <FontAwesomeIcon icon={faCheckCircle} />
                <span>Medicare Bulk Billing</span>
              </div>
              <div className="feature">
                <FontAwesomeIcon icon={faStethoscope} />
                <span>Comprehensive Care</span>
              </div>
              <div className="feature">
                <FontAwesomeIcon icon={faCalendarAlt} />
                <span>Easy Booking</span>
              </div>
            </div>
          </div>
          <div className="hero-image">
            <img 
              src="images/ffff.jpg" 
              alt="Medical clinic interior - bulk billing services" 
            />
          </div>
        </div>
      </section>

      {/* What is Bulk Billing */}
      <section className="what-is-bulk-billing" ref={whatIsRef} id="what-is">
        <div className="container">
          <div className="section-header">
            <h2>What is Bulk Billing?</h2>
            <p>Understanding Medicare bulk billing and how it benefits you</p>
          </div>
          <div className="content-grid">
            <div className="content-text">
              <h3>Direct Medicare Billing</h3>
              <p>
                Bulk billing means we bill Medicare directly for your consultation, so you don't pay 
                anything out-of-pocket. We accept the Medicare rebate as full payment for eligible 
                services, making General Practitioner care more accessible and affordable.
              </p>
              <h3>No Upfront Costs</h3>
              <p>
                For eligible patients, there are no consultation fees to pay. Simply present your 
                Medicare card and any relevant concession cards, and we'll handle the rest. This 
                removes financial barriers to essential General Practitioner services.
              </p>
              <h3>Quality Care Guaranteed</h3>
              <p>
                Bulk billing doesn't mean compromised care. Our experienced doctors provide the same 
                high-quality, comprehensive General Practitioner services to all patients, regardless of billing method.
              </p>
            </div>
            <div className="content-image">
              <img 
                src="images/Medicare-Bulk-Billing.jpg" 
                alt="Doctor consultation" 
              />
            </div>
          </div>
        </div>
      </section>

      {/* Eligibility Criteria */}
      <section className="eligibility-criteria" ref={criteriaRef} id="criteria">
        <div className="container">
          <div className="section-header">
            <h2>Who is Eligible for Bulk Billing?</h2>
            <p>Check if you qualify for our bulk billing services</p>
          </div>
          <div className="criteria-grid">
            {bulkBillingCriteria.map((criteria, index) => (
              <div key={index} className="criteria-card">
                <div className="criteria-icon" style={{ color: criteria.color }}>
                  <FontAwesomeIcon icon={criteria.icon} />
                </div>
                <h3>{criteria.title}</h3>
                <p>{criteria.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Services & Pricing */}
      <section className="services-pricing" ref={servicesRef} id="services">
        <div className="container">
          <div className="section-header">
            <h2>Bulk Billed Services & Pricing</h2>
            <p>Comprehensive General Practitioner services available through Medicare bulk billing</p>
          </div>
          <div className="pricing-table">
            <div className="table-header">
              <div className="service-col service-title">Service</div>
              <div className="bulk-col">Bulk Billing Status</div>
              <div className="rate-col rate-title">Private Rate</div>
            </div>
            {services.map((service, index) => (
              <div key={index} className="table-row">
                <div className="service-col">{service.service}</div>
                <div className="bulk-col">
                  <span className="bulk-badge">
                    <FontAwesomeIcon icon={faCheckCircle} />
                    {service.bulkBilled}
                  </span>
                </div>
                <div className="rate-col">{service.privateRate}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Requirements */}
      <section className="requirements" ref={requirementsRef} id="requirements">
        <div className="container">
          <div className="section-header">
            <h2>Requirements for Bulk Billing</h2>
            <p>What you need to bring for your bulk billed appointment</p>
          </div>
          <div className="requirements-content">
            <div className="requirements-list">
              {requirements.map((requirement, index) => (
                <div key={index} className="requirement-item">
                  <FontAwesomeIcon icon={faCheckCircle} />
                  <span>{requirement}</span>
                </div>
              ))}
            </div>
            <div className="requirements-image">
              <img 
                src="https://images.unsplash.com/photo-1584515933487-779824d29309?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=800&q=80" 
                alt="Medicare card and documents" 
              />
            </div>
          </div>
        </div>
      </section>

      {/* Procedure Fees */}
      <section className="procedure-fees" ref={procedureFeesRef} id="procedures">
        <div className="container">
          <div className="section-header">
            <h2>Procedure Fees (No Medicare Rebate)</h2>
            <p>Private fees for procedures and specialized services</p>
          </div>
          
          <div className="fees-note">
            <div className="note-card">
              <FontAwesomeIcon icon={faCheckCircle} />
              <p><strong>All other consults will be bulk billed for all patients</strong></p>
            </div>
          </div>

          <div className="fees-table-container">
            <table className="fees-table">
              <thead>
                <tr>
                  <th>Procedure/Item</th>
                  <th>Out of Pocket - Private Fees</th>
                </tr>
              </thead>
              <tbody>
                {procedureFees.map((item, index) => (
                  <tr key={index}>
                    <td>{item.procedure}</td>
                    <td className="fee-amount">{item.fee}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="additional-fees">
            <h3>Additional Fees</h3>
            <div className="fees-grid">
              {additionalFees.map((item, index) => (
                <div key={index} className="fee-card">
                  <span className="fee-service">{item.service}</span>
                  <span className="fee-amount">{item.fee}</span>
                </div>
              ))}
            </div>
          </div>

          <div className="workcover-info">
            <h3>WorkCover Claims</h3>
            <div className="workcover-grid">
              <div className="workcover-card">
                <FontAwesomeIcon icon={faCheckCircle} />
                <div>
                  <h4>Claim Number Available</h4>
                  <p>Bill consult directly to insurance</p>
                </div>
              </div>
              <div className="workcover-card">
                <FontAwesomeIcon icon={faCreditCard} />
                <div>
                  <h4>Claim Number Not Ready</h4>
                  <p>Bill to patient with receipt for insurance reimbursement</p>
                </div>
              </div>
            </div>
          </div>

          <div className="payment-notice">
            <div className="notice-card">
              <FontAwesomeIcon icon={faShieldAlt} />
              <div>
                <h4>Payment Policy</h4>
                <p>Payment is to be made at the time of procedures. Fees apply for all procedures and skin checks.</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Important Information */}
      <section className="important-info" ref={infoRef} id="info">
        <div className="container">
          <div className="info-grid">
            <div className="info-card">
              <div className="info-icon">
                <FontAwesomeIcon icon={faCreditCard} />
              </div>
              <h3>Payment Methods</h3>
              <p>
                For non-bulk billed services, we accept cash, EFTPOS, and credit cards. 
                Medicare claiming facilities are available on-site for your convenience.
              </p>
            </div>
            <div className="info-card">
              <div className="info-icon">
                <FontAwesomeIcon icon={faCalendarAlt} />
              </div>
              <h3>Booking Appointments</h3>
              <p>
                Book your bulk billed appointment online or call us directly. 
                Please mention your eligibility status when booking to ensure smooth processing.
              </p>
            </div>
            <div className="info-card">
              <div className="info-icon">
                <FontAwesomeIcon icon={faStethoscope} />
              </div>
              <h3>Quality Care</h3>
              <p>
                All our doctors are experienced general practitioners committed to providing 
                excellent General Practitioner care regardless of your billing arrangement.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Contact CTA */}
      <section className="contact-cta">
        <div className="container">
          <div className="cta-content">
            <h2>Ready to Book Your Bulk Billed Appointment?</h2>
            <p>Contact us today to schedule your consultation with one of our experienced doctors</p>
            <div className="cta-buttons">
              <a href="tel:+61249501333" className="cta-btn primary">
                <FontAwesomeIcon icon={faPhone} />
                Call Now: (02) 4950 1333
              </a>
              <a href="https://www.hotdoc.com.au/medical-centres/tea-gardens-NSW-2324/tea-gardens-gp/doctors" className="cta-btn secondary">Book Online</a>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default BulkBilling;
